import { AnnouncementType } from 'components/admin/Announcements/utils';
import { OOI_USE_TWILIO_VERIFY } from 'components/admin/utils/feature-flags';
import { useBanners } from 'components/GlobalBanners/globalBanners';
import { usePSLabs } from 'controllers/contexts/labsFeatures';
import { DateTime } from 'luxon';
import { useCallback, useEffect } from 'react';
import { BANNER_TYPES } from 'utils/events';
import { TFNHistoryEntry, useShopTFNHistory } from './hooks/useShopTFNHistory';
import { useRequestNewTFN } from './hooks/useTFNVerification';

const tfnInvalidationBannerId = 'tfnInvalidationBanner';

const CONTACT_SUPPORT_MAILTO = 'mailto:verifications@postscript.io';

function isTFNInvalidated(tfnHistory: TFNHistoryEntry[]) {
  if (!tfnHistory || tfnHistory.length === 0) {
    return false;
  }

  // Find the most recent unassignedAt timestamp
  let mostRecentUnassignedAt: DateTime | null = null;
  for (const entry of tfnHistory) {
    if (entry.unassignedAt) {
      const unassignedDate = DateTime.fromISO(entry.unassignedAt);
      if (!mostRecentUnassignedAt || unassignedDate > mostRecentUnassignedAt) {
        mostRecentUnassignedAt = unassignedDate;
      }
    }
  }

  // If there's no unassignedAt, TFN is not invalidated
  if (!mostRecentUnassignedAt) {
    return false;
  }

  // Check if any assignedAt is after the most recent unassignedAt
  for (const entry of tfnHistory) {
    if (entry.assignedAt) {
      const assignedDate = DateTime.fromISO(entry.assignedAt);
      if (assignedDate > mostRecentUnassignedAt) {
        // There's a newer assignment, so TFN is not currently invalidated
        return false;
      }
    }
  }

  // The most recent action was an unassignment, so TFN is invalidated
  return true;
}

export default function TFNInvalidationBanner(): null {
  const { addBanner, removeBanner } = useBanners();
  const { data, isFetched } = useShopTFNHistory();
  const { hasLabsFlag } = usePSLabs();
  const hasTwilioVerify = hasLabsFlag(OOI_USE_TWILIO_VERIFY);
  const { mutate: requestNewTFN, isLoading } = useRequestNewTFN();

  const handleRequestNewTFN = useCallback(() => {
    requestNewTFN(undefined, {
      onSuccess: () => {
        removeBanner(tfnInvalidationBannerId);
      },
    });
  }, [requestNewTFN, removeBanner]);

  const handleContactSupport = useCallback(() => {
    window.open(CONTACT_SUPPORT_MAILTO, '_blank', 'noopener,noreferrer');
  }, []);

  useEffect(() => {
    removeBanner(tfnInvalidationBannerId);

    // Skip banner if shop has ooi_use_twilio_verify feature flag
    if (hasTwilioVerify) {
      return;
    }

    if (!isFetched || !data?.history) {
      return;
    }

    const isInvalidated = isTFNInvalidated(data.history);

    if (isInvalidated) {
      addBanner({
        id: tfnInvalidationBannerId,
        announcementType: AnnouncementType.EMERGENCY,
        eventTrackingOptions: {
          bannerType: BANNER_TYPES.TFN_VERIFICATION_STATUS,
        },
        data: {
          collapseAt: 1200,
          variant: 'critical',
          heading: 'Your Toll-Free Number (TFN) has been invalidated',
          bodyText:
            'Your TFN has been unassigned and is no longer active. This may prevent you from sending messages to your subscribers.',
          primaryAction: {
            text: isLoading ? 'Requesting...' : 'Request New TFN',
            onClick: handleRequestNewTFN,
            disabled: isLoading,
          },
          secondaryAction: {
            text: 'Contact Support',
            onClick: handleContactSupport,
          },
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, isFetched, hasTwilioVerify, isLoading]);

  return null;
}
